import ExcelJS from 'exceljs';
import i18n from '../../../i18n';
import { BreakdownInfo } from '../../../pages/Home/types';
import { buildExportDownloadFileName } from '../exportFileName';
import { compareExportNamesDigitLetterPinyin, sortCastItemsByNO } from '../../../utils/elementSortUtils';
import { SlotInfo, SlotType } from '../../../types/schedule.types';
import { writeWorkbookBufferWithProgress } from '../excelWriteProgress';
import {
  SlotDataCache,
  ElementDataCache,
  ElementRelationDataCache
} from '../../../types/cache.types';
import { ElementInfo, ElementType } from '../../../types/element.types';

export type SupportedLanguage = 'zh-CN' | 'zh-HK' | 'en';

const t = (key: string, opts?: Record<string, unknown> & { lng?: string }) => i18n.t(key, opts);

const COLORS = {
  BLACK: '#000000',
  /** 表格外框及第 1–2、3–4 行间、B–C 列间分隔线 */
  BORDER_STRONG: '#000000',
  /** 其余内部网格线 */
  BORDER_INNER: '#AFB0B0',
  WHITE: '#FFFFFF',
  /** 间隔色 / 无拍摄任务日期列底色 */
  STRIPE_ALT: '#E9EAEA'
};

/** C 列起日期/数量列宽（约容纳 MM/DD 与 1–2 位数量） */
const DATE_COL_WIDTH = 5;

const ALL_ELEMENT_TYPES: ElementType[] = [
  ElementType.CAST,
  ElementType.FEATURED,
  ElementType.BACKGROUND,
  ElementType.STUNT_PLAYERS,
  ElementType.ANIMALS,
  ElementType.VEHICLES,
  ElementType.ART_PROPS,
  ElementType.WARDROBE,
  ElementType.HAIR_MAKEUP,
  ElementType.SFX_MU,
  ElementType.SFX,
  ElementType.VFX,
  ElementType.CAMERA,
  ElementType.LIGHTING,
  ElementType.SOUND,
  ElementType.ADDL_LABOR,
  ElementType.SPECIAL_EQUIPMENT,
  ElementType.MISC,
  ElementType.NOTE
];

const MAX_QTY_TYPES = new Set<ElementType>([
  ElementType.FEATURED,
  ElementType.BACKGROUND,
  ElementType.STUNT_PLAYERS,
  ElementType.ANIMALS,
  ElementType.VEHICLES
]);

const WEEKDAY_I18N_KEYS = [
  'weekdaySun',
  'weekdayMon',
  'weekdayTue',
  'weekdayWed',
  'weekdayThu',
  'weekdayFri',
  'weekdaySat'
] as const;

const WEEKDAY_ZH_SHORT = ['日', '一', '二', '三', '四', '五', '六'];

const SHEET_FREEZE_VIEWS: ExcelJS.WorksheetView[] = [
  {
    state: 'frozen',
    xSplit: 2,
    ySplit: 4,
    topLeftCell: 'C5',
    activeCell: 'C5'
  } as ExcelJS.WorksheetView
];

export const EXCEL_ELEMENT_SHOOTING_DATES_DETAIL_MARGINS_INCH = {
  left: 0.35,
  right: 0.35,
  top: 0.35,
  bottom: 0.5,
  header: 0.3,
  footer: 0.3
} as const;

const hexToArgb = (hex: string): string => `FF${hex.replace('#', '').toUpperCase()}`;

const thinBorder = (hex: string): ExcelJS.Border => ({
  style: 'thin',
  color: { argb: hexToArgb(hex) }
});

/**
 * 按单元格位置设置边框：内部线多为 #AFB0B0；第 1–2、3–4 行间与 B–C 列间为黑色。
 * 须在整张表写完后再调用，以保证 A 列等未单独 style 的格子也有网格线。
 */
function applySheetGridBorders(sheet: ExcelJS.Worksheet, lastRow: number, lastCol: number) {
  const strong = thinBorder(COLORS.BORDER_STRONG);
  const inner = thinBorder(COLORS.BORDER_INNER);

  for (let row = 1; row <= lastRow; row += 1) {
    for (let col = 1; col <= lastCol; col += 1) {
      const cell = sheet.getCell(row, col);
      const top =
        row === 1 || row === 2 || row === 4
          ? strong
          : row > 1
            ? inner
            : undefined;
      const bottom =
        row === 1 || row === 3
          ? strong
          : row < lastRow
            ? inner
            : strong;
      const left =
        col === 1 || col === COL_DATE_START
          ? strong
          : col > 1
            ? inner
            : undefined;
      const right =
        col === COL_NAME || col === lastCol
          ? strong
          : col < lastCol
            ? inner
            : undefined;

      cell.border = { top, left, bottom, right };
    }
  }
}

/** 表头/标签格：字号与对齐，无灰底（第 2–4 行）；边框由 applySheetGridBorders 统一绘制 */
const styleLabelCell = (cell: ExcelJS.Cell, horizontal: 'left' | 'center' = 'left') => {
  cell.font = { color: { argb: hexToArgb(COLORS.BLACK) }, size: 8, bold: false };
  cell.alignment = { vertical: 'middle', horizontal, wrapText: false };
};

const applyCellFill = (cell: ExcelJS.Cell, fillHex: string | null) => {
  if (fillHex) {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hexToArgb(fillHex) } };
  }
};

/** 数据行单元格（第 5 行起可设间隔底色） */
const styleDataCell = (
  cell: ExcelJS.Cell,
  horizontal: 'left' | 'center',
  fillHex: string | null = null
) => {
  applyCellFill(cell, fillHex);
  cell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
  cell.alignment = { vertical: 'middle', horizontal, wrapText: false };
};

/** 第 5 行起元素行间隔色：偶数行（0-based）白，奇数行 #E9EAEA */
const elementRowStripeFill = (elementRowIndex: number): string | null =>
  elementRowIndex % 2 === 1 ? COLORS.STRIPE_ALT : null;

/** 日期列底色：无拍摄任务日整列浅灰；否则跟随行间隔色 */
const dateColumnFill = (elementRowIndex: number, nonShootDay: boolean): string | null => {
  if (nonShootDay) return COLORS.STRIPE_ALT;
  return elementRowStripeFill(elementRowIndex);
};

function applyPrintLayout(worksheet: ExcelJS.Worksheet, language: SupportedLanguage) {
  // ExcelJS 写入 printTitlesRow/printTitlesColumn 会生成 _xlnm.Print_Titles 命名区域，
  // 多 Sheet + 中文表名时 Excel 常报「部分内容有问题」并删除 workbook 命名区域。
  // 固定 A/B 列与表头行请用冻结窗格；打印重复列需在 Excel 中手动设置「标题列」为 $A:$B。
  worksheet.pageSetup = {
    ...worksheet.pageSetup,
    orientation: 'landscape',
    horizontalCentered: true,
    margins: { ...EXCEL_ELEMENT_SHOOTING_DATES_DETAIL_MARGINS_INCH }
  };
  const footerBody = t('export.excelPageFooter', { lng: language });
  worksheet.headerFooter = {
    ...worksheet.headerFooter,
    oddFooter: `&C&8${footerBody}`
  };
}

/** 导出前移除会损坏 xlsx 的打印标题/区域命名（ExcelJS workbook.xml） */
function sanitizePrintSetupBeforeWrite(workbook: ExcelJS.Workbook) {
  workbook.worksheets.forEach((ws) => {
    const ps = ws.pageSetup;
    if (!ps) return;
    delete ps.printTitlesRow;
    delete ps.printTitlesColumn;
    delete ps.printArea;
  });
}

const normalizeDate = (raw?: string): string | null => {
  if (!raw) return null;
  const d = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
};

const MAX_EXCEL_CELL_TEXT_LENGTH = 32767;

const sanitizeExcelText = (raw: string): string => {
  const cleaned = Array.from(raw)
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      const isControl =
        (code >= 0 && code <= 8) ||
        code === 11 ||
        code === 12 ||
        (code >= 14 && code <= 31) ||
        code === 127;
      return !isControl;
    })
    .join('');
  if (cleaned.length <= MAX_EXCEL_CELL_TEXT_LENGTH) return cleaned;
  return cleaned.slice(0, MAX_EXCEL_CELL_TEXT_LENGTH);
};

const toSafeSheetName = (raw: string, used: Set<string>): string => {
  let base = sanitizeExcelText(raw || 'Sheet')
    .replace(/[\\/?*:[\]]/g, '_')
    .replace(/^'+|'+$/g, '')
    .trim()
    .slice(0, 31) || 'Sheet';
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  let i = 2;
  while (i < 100) {
    const suffix = `_${i}`;
    const candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
    i += 1;
  }
  return base;
};

function getShootingSortedDates(slots: SlotInfo[]): string[] {
  const dates = slots
    .filter((pt) => pt.slotType === SlotType.SHOOT || !pt.slotType)
    .map((pt) => normalizeDate(pt.slotDate || ''))
    .filter((d): d is string => Boolean(d));
  return Array.from(new Set(dates)).sort();
}

function buildShootDayByDate(slots: SlotInfo[]): Map<string, number> {
  const map = new Map<string, number>();
  getShootingSortedDates(slots).forEach((dateStr, index) => {
    map.set(dateStr, index + 1);
  });
  return map;
}

const formatYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** 开机～杀青连续日历日；无起止时用 SHOOT 任务日最早/最晚 */
function getExportCalendarDates(
  slots: SlotInfo[],
  scheduleStart?: string | null,
  scheduleEnd?: string | null
): string[] {
  let start = normalizeDate(scheduleStart || '');
  let end = normalizeDate(scheduleEnd || '');
  const shootDates = getShootingSortedDates(slots);
  if (!start && shootDates.length > 0) start = shootDates[0];
  if (!end && shootDates.length > 0) end = shootDates[shootDates.length - 1];
  if (!start || !end || start > end) return shootDates;

  const out: string[] = [];
  const cur = new Date(`${start}T12:00:00`);
  const endD = new Date(`${end}T12:00:00`);
  while (cur.getTime() <= endD.getTime()) {
    out.push(formatYmd(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Excel 列号（1-based）：A=序号，B=名称，C 起为日期 */
const COL_SERIAL = 1;
const COL_NAME = 2;
const COL_DATE_START = 3;

const dateArrayIndex = (dateIndex: number) => COL_DATE_START - 1 + dateIndex;

/** Excel 列号（1-based）转列字母，如 3→C、85→CG */
const colNumToLetter = (col: number): string => {
  let letter = '';
  let n = col;
  while (n > 0) {
    const rem = (n - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    n = Math.floor((n - 1) / 26);
  }
  return letter;
};

const formatMMDD = (iso: string): string => {
  const parts = iso.split('-');
  if (parts.length < 3) return iso;
  return `${parts[1]}/${parts[2]}`;
};

const formatWeekdayShort = (iso: string, language: SupportedLanguage): string => {
  const date = new Date(`${iso}T12:00:00`);
  const day = date.getDay();
  if (language === 'en') {
    return t(`schedule.${WEEKDAY_I18N_KEYS[day]}`, { lng: language }) as string;
  }
  return WEEKDAY_ZH_SHORT[day];
};

const textDisplayWidth = (raw: string): number => {
  let w = 0;
  for (const ch of raw) {
    w += ch.charCodeAt(0) > 255 ? 2 : 1;
  }
  return w;
};

/** 单元格 8pt；Excel 列宽单位按默认 11pt 字符宽计 */
const DETAIL_CELL_FONT_PT = 8;
const EXCEL_DEFAULT_COL_WIDTH_FONT_PT = 11;

/**
 * B 列宽度：仅按第 5 行起元素名称中最长内容自适应。
 * 全角/中文按 2、半角按 1 估算后，再按字号折算到 Excel 列宽单位。
 */
function fitNameColumnWidth(
  worksheet: ExcelJS.Worksheet,
  firstElementRow: number,
  lastDataRow: number,
  min = 8,
  max = 56
) {
  let maxW = 0;
  for (let row = firstElementRow; row <= lastDataRow; row += 1) {
    const v = worksheet.getCell(row, COL_NAME).value;
    const text = v == null ? '' : String(v);
    maxW = Math.max(maxW, textDisplayWidth(text));
  }
  const fontScale = DETAIL_CELL_FONT_PT / EXCEL_DEFAULT_COL_WIDTH_FONT_PT;
  worksheet.getColumn(COL_NAME).width = Math.min(
    max,
    Math.max(min, maxW * fontScale + 2)
  );
}

/** 分场「最大数量」：elementMaxQtyInBreakdown 与各场关联数量取大 */
function resolveElementMaxQtyInBreakdown(
  item: ElementInfo,
  maxQtyByItem: Map<string, number>
): number | null {
  const breakdown = item.elementMaxQtyInBreakdown;
  const relationMax = maxQtyByItem.get(item.elementID);
  let max = 0;
  if (breakdown != null && breakdown > max) max = breakdown;
  if (relationMax != null && relationMax > max) max = relationMax;
  return max > 0 ? max : null;
}

/** 各元素在各拍摄日的场次关联数量最大值（与 Excel-ElementQTY / 当日详情一致） */
function buildElementMaxQtyByDate(
  elementRelations: { elementID: string; sceneID: string; elementQtyInScene?: number }[],
  daySceneIds: Map<string, Set<string>>
): Map<string, Map<string, number>> {
  const qtyByElementScene = new Map<string, Map<string, number>>();
  elementRelations.forEach((rel) => {
    const qty = Math.max(1, rel.elementQtyInScene || 1);
    const sceneMap = qtyByElementScene.get(rel.elementID) || new Map<string, number>();
    const prev = sceneMap.get(rel.sceneID) || 0;
    if (qty > prev) sceneMap.set(rel.sceneID, qty);
    qtyByElementScene.set(rel.elementID, sceneMap);
  });

  const result = new Map<string, Map<string, number>>();
  daySceneIds.forEach((sceneIds, date) => {
    qtyByElementScene.forEach((sceneMap, elementId) => {
      let dayMax = 0;
      sceneIds.forEach((sceneId) => {
        const q = sceneMap.get(sceneId);
        if (q != null && q > dayMax) dayMax = q;
      });
      if (dayMax <= 0) return;
      const dateMap = result.get(elementId) || new Map<string, number>();
      dateMap.set(date, dayMax);
      result.set(elementId, dateMap);
    });
  });
  return result;
}

function formatElementDisplayName(
  type: ElementType,
  item: ElementInfo,
  maxQtyByItem: Map<string, number>
): string {
  const name = sanitizeExcelText((item.elementName || item.elementAbbr || '').trim());
  if (type === ElementType.CAST) {
    const abbr = sanitizeExcelText((item.elementAbbr || '').trim());
    if (abbr) return `${name}（${abbr}）`;
    return name;
  }
  if (MAX_QTY_TYPES.has(type)) {
    const qty = resolveElementMaxQtyInBreakdown(item, maxQtyByItem);
    if (qty != null) {
      return `${name}（${qty}）`;
    }
    return name;
  }
  return name;
}

type ElementSheetRow = {
  serial: string;
  displayName: string;
  maxQtyByDate: Map<string, number>;
};

export const exportElementShootingDatesDetailExcel = async (
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage = 'zh-CN',
  _secondaryLanguage?: SupportedLanguage,
  breakdownId?: string,
  effectiveScheduleId?: string | null,
  getSlotCache?: (scheduleId: string) => SlotDataCache | null,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null,
  onProgress?: (progress: number) => void
): Promise<void> => {
  onProgress?.(10);
  if (!breakdownId || !effectiveScheduleId || !getSlotCache || !getElementCache || !getElementRelationCache) {
    throw new Error(t('export.exportNeedScheduleId', { lng: language }) as string);
  }

  const slotCache = getSlotCache(effectiveScheduleId);
  const slots = slotCache?.slots || [];
  const elements = getElementCache(breakdownId)?.elements || [];
  const elementRelations = getElementRelationCache(breakdownId)?.elementRelations || [];

  const daySceneIds = new Map<string, Set<string>>();
  slots.forEach((pt) => {
    if (!(pt.slotType === SlotType.SHOOT || !pt.slotType)) return;
    if (!pt.slotBelongSceneID) return;
    const d = normalizeDate(pt.slotDate || '');
    if (!d) return;
    const set = daySceneIds.get(d) || new Set<string>();
    set.add(pt.slotBelongSceneID);
    daySceneIds.set(d, set);
  });

  const elementMaxQtyByDate = buildElementMaxQtyByDate(elementRelations, daySceneIds);

  const maxQtyByItem = new Map<string, number>();
  elementRelations.forEach((rel) => {
    const qty = Math.max(1, rel.elementQtyInScene || 1);
    const prev = maxQtyByItem.get(rel.elementID) || 0;
    if (qty > prev) maxQtyByItem.set(rel.elementID, qty);
  });

  const itemsByType = new Map<ElementType, ElementInfo[]>();
  elements.forEach((item) => {
    if (!item.elementType) return;
    const list = itemsByType.get(item.elementType) || [];
    list.push(item);
    itemsByType.set(item.elementType, list);
  });

  const scheduleInfo = slotCache?.scheduleInfo;
  const allCalendarDates = getExportCalendarDates(
    slots,
    scheduleInfo?.scheduleStartDate,
    scheduleInfo?.scheduleEndDate
  );
  const shootDayByDate = buildShootDayByDate(slots);
  const shootDateSet = new Set(getShootingSortedDates(slots));
  onProgress?.(35);

  const workbook = new ExcelJS.Workbook();
  const usedSheetNames = new Set<string>();
  const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') || '' : '';
  const scheduleName = slotCache?.scheduleInfo?.scheduleName || '';
  const scheduleVersionLabel = t('export.castPdfScheduleVersionLabel', { lng: language }) as string;
  const breakdownVersionLabel = t('export.castPdfBreakdownVersionLabel', { lng: language }) as string;
  const scriptVersionLabel = t('export.scriptVersion', { lng: language }) as string;
  const labelSeparator = t('common.labelSeparator', { lng: language }) as string;
  const pipeSeparator = t('common.pipeSeparator', { lng: language }) as string;
  const title = sanitizeExcelText(
    `《${filmName}》${scheduleVersionLabel}${labelSeparator}${scheduleName}${pipeSeparator}${breakdownVersionLabel}${labelSeparator}${breakdownInfo.breakdownName || ''}${pipeSeparator}${scriptVersionLabel}${labelSeparator}${breakdownInfo.breakdownScriptOverview || ''}`
  );

  const labelDate = t('export.matrixRowDate', { lng: language }) as string;
  const labelWeekday = t('export.matrixRowWeekday', { lng: language }) as string;
  const labelShootDay = t('export.shootDay', { lng: language }) as string;
  const labelSerial = t('export.serialNumber', { lng: language }) as string;
  const labelColumnTotal = t('export.columnTotal', { lng: language }) as string;
  const labelRowSubtotal = t('export.rowSubtotal', { lng: language }) as string;

  const dateColCount = allCalendarDates.length;
  const lastDateCol = Math.max(COL_DATE_START, COL_DATE_START + dateColCount - 1);
  const subtotalCol = lastDateCol + 1;
  const lastSheetCol = subtotalCol;
  const lastDateColLetter = colNumToLetter(lastDateCol);

  ALL_ELEMENT_TYPES.forEach((type, idx) => {
    const typeLabel = sanitizeExcelText(t(`elementType.${type}`, { lng: language }) as string);
    const sheet = workbook.addWorksheet(toSafeSheetName(typeLabel, usedSheetNames));
    sheet.views = SHEET_FREEZE_VIEWS;

    sheet.getColumn(COL_SERIAL).width = 5;
    for (let col = COL_DATE_START; col < COL_DATE_START + dateColCount; col += 1) {
      sheet.getColumn(col).width = DATE_COL_WIDTH;
    }

    sheet.addRow([title]);
    const titleCell = sheet.getCell(1, COL_SERIAL);
    titleCell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
    for (let col = COL_NAME; col <= lastSheetCol; col += 1) {
      sheet.getCell(1, col).value = null;
    }

    const row2Values: (string | number)[] = new Array(lastSheetCol).fill('');
    row2Values[COL_NAME - 1] = labelDate;
    allCalendarDates.forEach((d, i) => {
      row2Values[dateArrayIndex(i)] = formatMMDD(d);
    });
    sheet.addRow(row2Values);

    const row3Values: (string | number)[] = new Array(lastSheetCol).fill('');
    row3Values[COL_NAME - 1] = labelWeekday;
    allCalendarDates.forEach((d, i) => {
      row3Values[dateArrayIndex(i)] = formatWeekdayShort(d, language);
    });
    sheet.addRow(row3Values);

    const row4Values: (string | number)[] = new Array(lastSheetCol).fill('');
    row4Values[COL_SERIAL - 1] = labelSerial;
    row4Values[COL_NAME - 1] = labelShootDay;
    allCalendarDates.forEach((d, i) => {
      row4Values[dateArrayIndex(i)] = shootDateSet.has(d) ? (shootDayByDate.get(d) ?? '') : '';
    });
    sheet.addRow(row4Values);

    for (let r = 2; r <= 4; r += 1) {
      const row = sheet.getRow(r);
      styleLabelCell(row.getCell(COL_SERIAL), r === 4 ? 'center' : 'left');
      styleLabelCell(row.getCell(COL_NAME), 'left');
      for (let i = 0; i < dateColCount; i += 1) {
        const dateStr = allCalendarDates[i];
        const nonShootDay = !shootDateSet.has(dateStr);
        if (r === 4 && nonShootDay) {
          styleDataCell(row.getCell(COL_DATE_START + i), 'center', COLORS.STRIPE_ALT);
        } else {
          styleLabelCell(row.getCell(COL_DATE_START + i), 'center');
        }
      }
    }

    sheet.getCell(4, subtotalCol).value = labelRowSubtotal;
    styleLabelCell(sheet.getCell(4, subtotalCol), 'center');
    sheet.getColumn(subtotalCol).width = DATE_COL_WIDTH;

    const currentItems = [...(itemsByType.get(type) || [])];
    if (type === ElementType.CAST) {
      const castSorted = sortCastItemsByNO(currentItems);
      currentItems.length = 0;
      currentItems.push(...castSorted);
    } else {
      currentItems.sort((a, b) => compareExportNamesDigitLetterPinyin(
        (a.elementName || a.elementAbbr || '').trim(),
        (b.elementName || b.elementAbbr || '').trim()
      ));
    }

    const sheetRows: ElementSheetRow[] = currentItems.map((item, index) => {
      const serial = type === ElementType.CAST
        ? (item.elementNO || '')
        : String(index + 1);
      return {
        serial,
        displayName: formatElementDisplayName(type, item, maxQtyByItem),
        maxQtyByDate: elementMaxQtyByDate.get(item.elementID) || new Map()
      };
    });

    sheetRows.forEach((rowData, elementRowIndex) => {
      const values: (string | number)[] = new Array(lastSheetCol).fill('');
      values[COL_SERIAL - 1] = rowData.serial;
      values[COL_NAME - 1] = rowData.displayName;
      allCalendarDates.forEach((d, i) => {
        const qty = rowData.maxQtyByDate.get(d);
        values[dateArrayIndex(i)] = qty != null ? qty : '';
      });
      const row = sheet.addRow(values);
      const rowFill = elementRowStripeFill(elementRowIndex);
      styleDataCell(row.getCell(COL_SERIAL), 'center', rowFill);
      styleDataCell(row.getCell(COL_NAME), 'left', rowFill);
      for (let i = 0; i < dateColCount; i += 1) {
        const nonShootDay = !shootDateSet.has(allCalendarDates[i]);
        styleDataCell(
          row.getCell(COL_DATE_START + i),
          'center',
          dateColumnFill(elementRowIndex, nonShootDay)
        );
      }
    });

    const firstElementRow = 5;
    const lastDataRow = 4 + sheetRows.length;
    let lastRow = lastDataRow;

    if (sheetRows.length > 0) {
      for (let row = firstElementRow; row <= lastDataRow; row += 1) {
        const elementRowIndex = row - firstElementRow;
        const subtotalCell = sheet.getCell(row, subtotalCol);
        subtotalCell.value = {
          formula: `SUM(C${row}:${lastDateColLetter}${row})`
        };
        styleDataCell(subtotalCell, 'center', elementRowStripeFill(elementRowIndex));
      }

      const totalRow = lastDataRow + 1;
      lastRow = totalRow;
      sheet.getCell(totalRow, COL_NAME).value = labelColumnTotal;
      styleLabelCell(sheet.getCell(totalRow, COL_NAME), 'left');

      for (let col = COL_DATE_START; col <= lastDateCol; col += 1) {
        const colLetter = colNumToLetter(col);
        const totalCell = sheet.getCell(totalRow, col);
        totalCell.value = {
          formula: `SUM(${colLetter}${firstElementRow}:${colLetter}${lastDataRow})`
        };
        styleDataCell(totalCell, 'center');
      }

      const grandTotalCell = sheet.getCell(totalRow, subtotalCol);
      grandTotalCell.value = {
        formula: `SUM(C${firstElementRow}:${lastDateColLetter}${lastDataRow})`
      };
      styleDataCell(grandTotalCell, 'center');

      fitNameColumnWidth(sheet, firstElementRow, lastDataRow);
    }

    applySheetGridBorders(sheet, lastRow, lastSheetCol);
    applyPrintLayout(sheet, language);

    const progress = 35 + Math.round(((idx + 1) / ALL_ELEMENT_TYPES.length) * 40);
    onProgress?.(Math.min(75, progress));
  });

  const fileName = buildExportDownloadFileName({
    lng: language,
    filmName,
    optionDisplayName: t('export.elementsShootingDatesDetailTable', { lng: language }) as string,
    optionFallbackI18nKey: 'export.elementsShootingDatesDetailTable',
    breakdownDisplayName: scheduleName,
    breakdownFallbackI18nKey: 'export.schedule',
    extension: 'xlsx'
  });

  sanitizePrintSetupBeforeWrite(workbook);
  const buffer = await writeWorkbookBufferWithProgress(workbook, onProgress, { start: 80, end: 98 });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  onProgress?.(100);
};
